import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  AvatarGroup,
  Chip,
  CircularProgress,
  Collapse,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Skeleton,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material'
import {
  EmojiEvents as TrophyIcon,
  Schedule as ScheduleIcon,
  CalendarMonth as CalendarIcon,
  TrendingUp as TrendingIcon,
  History as HistoryIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Group as GroupIcon,
  VisibilityOff as AnonymousIcon,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { useAdventureLeaderboard } from '../hooks/useAdventureLeaderboard'
import { useAuthStore } from '@store/authStore'
import { leaderboardApi } from '@api/endpoints/leaderboard'
import { sessionsApi } from '@api/endpoints/sessions'
import { LeaderboardTimeframe, LeaderboardSessionType, LeaderboardEntry } from '@api/types/leaderboard'
import { AdventureSessionDto } from '@api/types/adventure'
import { SessionStatus, SessionStateDto } from '@api/types/session'

interface AdventureLeaderboardProps {
  adventureId: string
  sessions?: AdventureSessionDto[]
}

type ViewMode = 'leaderboard' | 'sessions'

export function AdventureLeaderboard({ adventureId, sessions }: AdventureLeaderboardProps) {
  const navigate = useNavigate()
  const [timeframe, setTimeframe] = useState<LeaderboardTimeframe>('all')
  const [sessionType, setSessionType] = useState<LeaderboardSessionType>('all')
  const [viewMode, setViewMode] = useState<ViewMode>('leaderboard')
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null)
  const [expandedMySessionId, setExpandedMySessionId] = useState<string | null>(null)
  const [sessionsDisplayCount, setSessionsDisplayCount] = useState(5)
  const [sessionStateCache, setSessionStateCache] = useState<Record<string, SessionStateDto>>({})
  const [sessionStateLoading, setSessionStateLoading] = useState<string | null>(null)
  const { user } = useAuthStore()
  const { data, isLoading, error } = useAdventureLeaderboard(adventureId, { timeframe, type: sessionType })

  // Fetch user's rank across all timeframes
  const { data: myRankData } = useQuery({
    queryKey: ['my-rank', adventureId],
    queryFn: () => leaderboardApi.getMyRank(adventureId),
    enabled: !!user && !!adventureId,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  })

  const hasSessions = sessions && sessions.length > 0

  // Get the user's rank for the current timeframe
  const currentRank = myRankData?.ranksByTimeframe?.[timeframe]

  // Format duration from "d.HH:mm:ss.fffffff" or "HH:mm:ss.fffffff" or seconds
  const formatDuration = (duration: string | number | undefined | null): string => {
    if (!duration) return '--:--'
    if (typeof duration === 'number') {
      const hours = Math.floor(duration / 3600)
      const minutes = Math.floor((duration % 3600) / 60)
      const seconds = Math.floor(duration % 60)
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      }
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    }
    // String format - handle "d.HH:mm:ss.fffffff" format
    let durationStr = duration
    let days = 0

    // Check for days prefix (e.g., "7.09:32:29.5980173")
    if (durationStr.includes('.') && durationStr.indexOf('.') < durationStr.indexOf(':')) {
      const dayParts = durationStr.split('.')
      days = parseInt(dayParts[0], 10)
      durationStr = dayParts.slice(1).join('.')
    }

    // Strip fractional seconds
    const parts = durationStr.split('.')
    const timeParts = parts[0].split(':')

    if (timeParts.length === 3) {
      const hours = parseInt(timeParts[0], 10) + (days * 24)
      const minutes = timeParts[1]
      const seconds = timeParts[2]

      if (hours === 0) {
        return `${minutes}:${seconds}`
      }
      return `${hours}:${minutes}:${seconds}`
    }
    return parts[0]
  }

  // Get session status info
  const getSessionStatusInfo = (session: AdventureSessionDto) => {
    if (session.status === SessionStatus.InProgress) {
      return { label: 'In Progress', color: 'info' as const, icon: <PlayIcon fontSize="small" /> }
    }
    if (session.completedAt) {
      // Status 5 appears to be "Completed" based on data
      return { label: 'Completed', color: 'success' as const, icon: <CheckIcon fontSize="small" /> }
    }
    return { label: 'Abandoned', color: 'default' as const, icon: <CancelIcon fontSize="small" /> }
  }

  // Parse dates from backend as UTC
  const parseUtc = (dateStr: string | null | undefined): Date => {
    if (!dateStr) return new Date(0)
    if (dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('-', 10)) {
      return new Date(dateStr)
    }
    return new Date(dateStr + 'Z')
  }

  const getMedalColor = (position: number): string => {
    switch (position) {
      case 1: return '#FFD700' // Gold
      case 2: return '#C0C0C0' // Silver
      case 3: return '#CD7F32' // Bronze
      default: return ''
    }
  }

  const handleTimeframeChange = (
    _: React.MouseEvent<HTMLElement>,
    newTimeframe: LeaderboardTimeframe | null
  ) => {
    if (newTimeframe) {
      setTimeframe(newTimeframe)
    }
  }

  const handleSessionTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    newType: LeaderboardSessionType | null
  ) => {
    if (newType) {
      setSessionType(newType)
    }
  }

  const handleMySessionClick = async (sessionId: string) => {
    if (expandedMySessionId === sessionId) {
      setExpandedMySessionId(null)
      return
    }
    setExpandedMySessionId(sessionId)
    // Fetch session state if not already cached
    if (!sessionStateCache[sessionId]) {
      setSessionStateLoading(sessionId)
      try {
        const state = await sessionsApi.getSessionState(sessionId)
        setSessionStateCache(prev => ({ ...prev, [sessionId]: state }))
      } catch (err) {
        console.error('Failed to fetch session state:', err)
      } finally {
        setSessionStateLoading(null)
      }
    }
  }

  // Render the view mode toggle header
  const renderHeader = () => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
      {/* View mode selector - compact button with dropdown */}
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => hasSessions ? setMenuAnchor(e.currentTarget) : null}
        startIcon={viewMode === 'leaderboard' ? <TrophyIcon color="warning" /> : <HistoryIcon />}
        endIcon={hasSessions ? <ArrowDownIcon /> : undefined}
        sx={{
          textTransform: 'none',
          minWidth: 'auto',
          whiteSpace: 'nowrap',
        }}
      >
        {viewMode === 'leaderboard' ? 'Fastest' : 'My Sessions'}
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          selected={viewMode === 'leaderboard'}
          onClick={() => { setViewMode('leaderboard'); setMenuAnchor(null) }}
        >
          <ListItemIcon>
            <TrophyIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText>Fastest Times</ListItemText>
        </MenuItem>
        <MenuItem
          selected={viewMode === 'sessions'}
          onClick={() => { setViewMode('sessions'); setMenuAnchor(null) }}
        >
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>My Sessions</ListItemText>
        </MenuItem>
      </Menu>

      {/* Session type filter */}
      {viewMode === 'leaderboard' && (
        <ToggleButtonGroup
          value={sessionType}
          exclusive
          onChange={handleSessionTypeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 1,
              py: 0.5,
            },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="solo">Solo</ToggleButton>
          <ToggleButton value="group">Group</ToggleButton>
        </ToggleButtonGroup>
      )}

      {/* Timeframe selector */}
      <ToggleButtonGroup
        value={viewMode === 'leaderboard' ? timeframe : null}
        exclusive
        onChange={handleTimeframeChange}
        size="small"
        disabled={viewMode !== 'leaderboard'}
        sx={{
          '& .MuiToggleButton-root': {
            px: 1,
            py: 0.5,
          },
        }}
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="1m">Month</ToggleButton>
        <ToggleButton value="1w">Week</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  )

  // Render My Sessions view
  const renderSessions = () => {
    if (!sessions || sessions.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            No sessions yet. Start your first adventure!
          </Typography>
        </Box>
      )
    }

    // Sort sessions by startedAt descending (most recent first)
    const sortedSessions = [...sessions].sort(
      (a, b) => parseUtc(b.startedAt).getTime() - parseUtc(a.startedAt).getTime()
    )

    const visibleSessions = sortedSessions.slice(0, sessionsDisplayCount)
    const hasMore = sortedSessions.length > sessionsDisplayCount

    return (
      <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Started</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleSessions.map((session) => {
              const statusInfo = getSessionStatusInfo(session)
              const isActive = session.status === SessionStatus.InProgress
              const isExpanded = expandedMySessionId === session.id
              const cachedState = sessionStateCache[session.id]
              const isLoadingState = sessionStateLoading === session.id

              return (
                <React.Fragment key={session.id}>
                  <TableRow
                    onClick={() => handleMySessionClick(session.id)}
                    sx={{
                      bgcolor: isActive ? 'info.50' : 'inherit',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      '& > td': isExpanded ? { borderBottom: 'none' } : undefined,
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {parseUtc(session.startedAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {parseUtc(session.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={statusInfo.icon}
                        label={statusInfo.label}
                        size="small"
                        color={statusInfo.color}
                        variant={isActive ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Typography
                          fontWeight={statusInfo.label === 'Completed' ? 'bold' : 'normal'}
                          color={statusInfo.label === 'Completed' ? 'primary' : 'text.secondary'}
                        >
                          {formatDuration(session.duration)}
                        </Typography>
                        {isExpanded ? (
                          <ExpandLessIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded detail row */}
                  <TableRow>
                    <TableCell colSpan={3} sx={{ py: 0, px: 0, border: 'none' }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            px: 2,
                            py: 1.5,
                            mx: 1,
                            mb: 1,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                          }}
                        >
                          {isLoadingState ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                              <CircularProgress size={20} />
                            </Box>
                          ) : cachedState ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', gap: 2, textAlign: 'center' }}>
                              <Box sx={{ flex: '1 1 0', minWidth: 60 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Score Time
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="primary.main">
                                  {formatDuration(cachedState.effectiveDuration || cachedState.elapsedSeconds)}
                                </Typography>
                              </Box>
                              <Box sx={{ flex: '1 1 0', minWidth: 60 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Total Time
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {formatDuration(session.duration)}
                                </Typography>
                              </Box>
                              {cachedState.timePenaltyFromCluesSeconds != null && cachedState.timePenaltyFromCluesSeconds > 0 && (
                                <Box sx={{ flex: '1 1 0', minWidth: 60 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Clue Penalty
                                  </Typography>
                                  <Typography variant="body2" fontWeight={500} color="warning.main">
                                    +{formatDuration(cachedState.timePenaltyFromCluesSeconds)}
                                  </Typography>
                                </Box>
                              )}
                              <Box sx={{ flex: '1 1 0', minWidth: 60 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Progress
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {cachedState.progressPercent}%
                                </Typography>
                              </Box>
                              {session.completedAt && (
                                <Box sx={{ flex: '1 1 0', minWidth: 80 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Completed
                                  </Typography>
                                  <Typography variant="body2" fontWeight={500}>
                                    {parseUtc(session.completedAt).toLocaleString([], {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                              Failed to load session details
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {(hasMore || sessionsDisplayCount > 5) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
          {hasMore && (
            <Button
              size="small"
              onClick={() => setSessionsDisplayCount(prev => prev + 5)}
              endIcon={<ExpandMoreIcon />}
              sx={{ textTransform: 'none' }}
            >
              Show More ({sortedSessions.length - sessionsDisplayCount} remaining)
            </Button>
          )}
          {sessionsDisplayCount > 5 && (
            <Button
              size="small"
              onClick={() => setSessionsDisplayCount(5)}
              endIcon={<ExpandLessIcon />}
              sx={{ textTransform: 'none' }}
            >
              Show Less
            </Button>
          )}
        </Box>
      )}
      </>
    )
  }

  // Show sessions view even while leaderboard is loading
  if (viewMode === 'sessions') {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        {renderHeader()}
        {renderSessions()}
        {sessions && sessions.length > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {sessions.length} session{sessions.length !== 1 ? 's' : ''} total
            </Typography>
          </Box>
        )}
      </Paper>
    )
  }

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        {renderHeader()}
        <Skeleton variant="rectangular" height={200} />
      </Paper>
    )
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        {renderHeader()}
        <Alert severity="error">Failed to load leaderboard</Alert>
      </Paper>
    )
  }

  if (!data) {
    return null
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, overflow: 'hidden' }}>
      {renderHeader()}

      {/* Leaderboard Table */}
      {data.entries && data.entries.length > 0 ? (
        <TableContainer>
          <Table size="small" sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 36, px: 1 }}>#</TableCell>
                <TableCell sx={{ px: 1 }}>Adventurer</TableCell>
                <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>Time</TableCell>
                <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.entries.map((entry: LeaderboardEntry) => {
                const isGroupEntry = entry.isGroup
                const isAnonymous = entry.isAnonymous
                // Support both new API format (entry.user) and old format (entry.displayName)
                const entryUser = entry.user || (entry as any)
                const displayName = isAnonymous
                  ? 'Anonymous'
                  : isGroupEntry
                    ? entry.groupName || 'Group'
                    : entryUser.displayName || (entryUser as any).username || 'Unknown'
                const avatarUrl = isAnonymous ? undefined : entryUser.avatarUrl
                const userId = isAnonymous ? undefined : entryUser.id
                const position = entry.position ?? (entry as any).rank ?? 0
                const isCurrentUser = !isAnonymous && user?.userId && (userId === user.userId || (entry as any).odUserId === user.userId)
                const medalColor = getMedalColor(position)
                // Support both new (duration/durationFormatted) and old (completionTime) formats
                const duration = entry.durationFormatted || entry.duration || (entry as any).completionTime
                const isExpanded = expandedSessionId === entry.sessionId

                return (
                  <React.Fragment key={entry.sessionId}>
                    <TableRow
                      onClick={() => setExpandedSessionId(isExpanded ? null : entry.sessionId)}
                      sx={{
                        bgcolor: isCurrentUser ? 'primary.50' : 'inherit',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        // Remove bottom border when expanded so detail row connects
                        '& > td': isExpanded ? { borderBottom: 'none' } : undefined,
                      }}
                    >
                      <TableCell sx={{ px: 1 }}>
                        {medalColor ? (
                          <TrophyIcon sx={{ color: medalColor, fontSize: 18 }} />
                        ) : (
                          <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            {position}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ px: 1, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          {isAnonymous ? (
                            <Avatar sx={{ width: 24, height: 24, flexShrink: 0, bgcolor: 'grey.400' }}>
                              <AnonymousIcon sx={{ fontSize: 16 }} />
                            </Avatar>
                          ) : isGroupEntry ? (
                            <Avatar sx={{ width: 24, height: 24, flexShrink: 0, bgcolor: 'secondary.main' }}>
                              <GroupIcon sx={{ fontSize: 16 }} />
                            </Avatar>
                          ) : (
                            <Avatar
                              sx={{ width: 24, height: 24, flexShrink: 0, cursor: 'pointer' }}
                              src={avatarUrl}
                              onClick={(e) => {
                                e.stopPropagation()
                                if (userId) navigate(`/profile/${userId}`)
                              }}
                            >
                              {displayName?.[0] || '?'}
                            </Avatar>
                          )}
                          <Typography
                            variant="body2"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (!isAnonymous && !isGroupEntry && userId) navigate(`/profile/${userId}`)
                            }}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontWeight: isCurrentUser ? 600 : 400,
                              fontStyle: isAnonymous ? 'italic' : 'normal',
                              cursor: !isAnonymous && !isGroupEntry && userId ? 'pointer' : 'default',
                              '&:hover': !isAnonymous && !isGroupEntry && userId
                                ? { textDecoration: 'underline', color: 'primary.main' }
                                : undefined,
                            }}
                          >
                            {displayName}
                          </Typography>
                          {isGroupEntry && (
                            <Chip label="Group" size="small" variant="outlined" color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ px: 1 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary" noWrap>
                          {typeof duration === 'string' && duration.includes(' ') ? duration : formatDuration(duration)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ px: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {entry.completedAt ? parseUtc(entry.completedAt).toLocaleDateString() : '--'}
                          </Typography>
                          {isExpanded ? (
                            <ExpandLessIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expanded detail row */}
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 0, px: 0, border: 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              px: 2,
                              py: 1.5,
                              mx: 1,
                              mb: 1,
                              bgcolor: 'action.hover',
                              borderRadius: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', gap: 2, textAlign: 'center' }}>
                              {entry.carriedOverPenaltySeconds != null && entry.carriedOverPenaltySeconds > 0 && (
                                <Box sx={{ flex: '1 1 0', minWidth: 70 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Carried Over
                                  </Typography>
                                  <Typography variant="body2" fontWeight={500} color="warning.main">
                                    +{formatDuration(entry.carriedOverPenaltySeconds)}
                                  </Typography>
                                </Box>
                              )}
                              <Box sx={{ flex: '1 1 0', minWidth: 60 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Clues Used
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {entry.cluesUsed ?? 0}
                                </Typography>
                              </Box>
                              {entry.pausedSeconds != null && entry.pausedSeconds > 0 && (
                                <Box sx={{ flex: '1 1 0', minWidth: 60 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Paused
                                  </Typography>
                                  <Typography variant="body2" fontWeight={500}>
                                    {formatDuration(entry.pausedSeconds)}
                                  </Typography>
                                </Box>
                              )}
                              {entry.timeModifierSeconds != null && entry.timeModifierSeconds !== 0 && (
                                <Box sx={{ flex: '1 1 0', minWidth: 60 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Time Modifier
                                  </Typography>
                                  <Typography variant="body2" fontWeight={500} color={entry.timeModifierSeconds > 0 ? 'warning.main' : 'success.main'}>
                                    {entry.timeModifierSeconds > 0 ? '+' : ''}{formatDuration(Math.abs(entry.timeModifierSeconds))}
                                  </Typography>
                                </Box>
                              )}
                              {entry.cluePenaltySeconds != null && entry.cluePenaltySeconds > 0 && (
                                <Box sx={{ flex: '1 1 0', minWidth: 60 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Clue Penalty
                                  </Typography>
                                  <Typography variant="body2" fontWeight={500} color="warning.main">
                                    +{formatDuration(entry.cluePenaltySeconds)}
                                  </Typography>
                                </Box>
                              )}
                              <Box sx={{ flex: '1 1 0', minWidth: 60 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Time
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="primary.main">
                                  {typeof duration === 'string' && duration.includes(' ') ? duration : formatDuration(duration)}
                                </Typography>
                              </Box>
                              <Box sx={{ flex: '1 1 0', minWidth: 60 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Completed
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {entry.completedAt
                                    ? parseUtc(entry.completedAt).toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : '--'}
                                </Typography>
                              </Box>
                            </Box>
                            {/* Group participants */}
                            {isGroupEntry && entry.participants && entry.participants.length > 0 && (
                              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                  Participants
                                </Typography>
                                <AvatarGroup
                                  max={8}
                                  sx={{
                                    justifyContent: 'flex-start',
                                    '& .MuiAvatar-root': { width: 28, height: 28, fontSize: '0.75rem' },
                                  }}
                                >
                                  {entry.participants.map((p, i) =>
                                    p ? (
                                      <Tooltip key={p.id || i} title={p.displayName || 'Unknown'}>
                                        <Avatar
                                          src={p.avatarUrl}
                                          sx={{ cursor: 'pointer' }}
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            if (p.id) navigate(`/profile/${p.id}`)
                                          }}
                                        >
                                          {p.displayName?.[0] || '?'}
                                        </Avatar>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip key={i} title="Unknown participant">
                                        <Avatar>?</Avatar>
                                      </Tooltip>
                                    )
                                  )}
                                </AvatarGroup>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            No completions yet. Be the first!
          </Typography>
        </Box>
      )}

      {/* Footer: Total entries and user's rank */}
      <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        {data.totalEntries > 0 && (
          <Typography variant="caption" color="text.secondary">
            {data.totalEntries} completion{data.totalEntries !== 1 ? 's' : ''}
          </Typography>
        )}
        {currentRank && currentRank.position && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PersonIcon sx={{ fontSize: 14, color: 'primary.main' }} />
            <Typography variant="caption" color="primary.main" fontWeight={600}>
              #{currentRank.position} of {currentRank.totalPlayers}
            </Typography>
            {currentRank.bestTimeFormatted && (
              <Typography variant="caption" color="text.secondary">
                ({currentRank.bestTimeFormatted})
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  )
}
